/**
 * Escherllate ↔ Procreate PSD layer schema (group-based, v2 "Window/stencil").
 * One Procreate Layer Group per tile instance. Machine identity travels via
 * structure + the QR baked into the group's `Window` layer, NEVER the group
 * name — Procreate copies names verbatim when a group is duplicated, so names
 * can't be a key. Group names are purely a human-facing convenience.
 *
 * v2 containment is a `Window` STENCIL at the TOP of the group: opaque
 * everywhere except a transparent hole matching the tile shape (inverse of v1's
 * bottom `Mask` clip-base). Anything beneath it is automatically covered outside
 * the hole — no per-layer clipping toggle, no chain to break. The Window's large
 * permanently-opaque region also carries the baked instructional text + the
 * per-group identity QR, so no separate hidden data layer is needed (which also
 * removes v1's "does Procreate keep a hidden layer?" risk — Window is visible).
 *
 * This is the pure, DOM-free core: reserved child-layer names, the cosmetic
 * group-name cascade, the per-group instance-descriptor (de)serialisation, and
 * the baked-in instruction text. QR pixel encode/decode lives in psdData.ts;
 * canvas drawing in psdExport.ts; pixel reading + compositing in psdImport.ts.
 */

/** Reserved child-layer names within an instance group (case-sensitive, fixed).
 *  Window/Outline are locked guides whose PIXELS are excluded from the imported
 *  artwork; Paint (and any user-added siblings) are the artwork. The Window also
 *  carries the identity QR + shape hole, read separately from the composite. */
export const WINDOW_LAYER = 'Window';
export const OUTLINE_LAYER = 'Outline';
/** Two separate tile-separation outline layers (black + white), both hidden by
 *  default so the artist switches on whichever reads against their artwork. */
export const OUTLINE_BLACK_LAYER = 'Outline (black)';
export const OUTLINE_WHITE_LAYER = 'Outline (white)';
export const PAINT_LAYER = 'Paint';

const RESERVED = new Set<string>([WINDOW_LAYER, OUTLINE_LAYER, OUTLINE_BLACK_LAYER, OUTLINE_WHITE_LAYER]);

/** True for a group child whose pixels must never enter the imported artwork
 *  composite (Window stencil / Outline guide) — excluded by exact name
 *  regardless of visibility (brief: reserved-name exclusion is absolute). */
export function isReservedLayerName(name: string | undefined): boolean {
  return name !== undefined && RESERVED.has(name);
}

/** One tile instance = a (shape, style) pair, both 1-based. `style` is 1 for a
 *  single-style project. */
export interface InstanceDescriptor {
  shape: number;
  style: number;
}

/**
 * Cosmetic group name (brief cascade):
 *   1. nickname if set, else `Shape {i}` — but only when i > 1
 *   2. `Style {n}` — only when the project has more than one style
 *   3. join surviving segments with ` - `
 *   4. nothing survives → `PaintMe`
 * Never carries redundant info (single-style hides the style suffix; a
 * first/only instance with no nickname hides the shape index). Never parsed back.
 */
export function groupName(opts: { nickname?: string; shape: number; styleCount: number; style: number }): string {
  const segs: string[] = [];
  const nick = opts.nickname?.trim();
  if (nick) segs.push(nick);
  else if (opts.shape > 1) segs.push(`Shape ${opts.shape}`);
  if (opts.styleCount > 1) segs.push(`Style ${opts.style}`);
  return segs.length > 0 ? segs.join(' - ') : 'PaintMe';
}

/**
 * The full instructional sentence baked into the FIRST exported instance's
 * Window (brief). `name` is the group's cascade name; pass null for the trivial
 * fallback case (the one where the group name itself falls back to `PaintMe`) —
 * "Tessellating" reads in prose where "PaintMe" wouldn't. Later instances bake
 * just their own group name instead of this sentence.
 */
export function instructionText(name: string | null): string {
  return name === null
    ? 'Anything you draw in the visible window of this layer group will be flattened into the tessellating image.'
    : `Anything you draw in the visible window of this layer group will be flattened and make up the image for "${name}".`;
}

/** Instance descriptor → compact QR payload string (baked into the Window).
 *  Kept tiny so it fits one QR module matrix. */
export function serializeInstance(d: InstanceDescriptor): string {
  return JSON.stringify({ s: d.shape, y: d.style });
}

/** Parse a Window QR payload back to a descriptor, or null if malformed.
 *  `style` defaults to 1 when absent (single-style project). */
export function parseInstance(payload: string | null): InstanceDescriptor | null {
  if (!payload) return null;
  try {
    const o = JSON.parse(payload) as { s?: unknown; y?: unknown };
    const shape = typeof o.s === 'number' && Number.isInteger(o.s) && o.s >= 1 ? o.s : null;
    if (shape === null) return null;
    const style = typeof o.y === 'number' && Number.isInteger(o.y) && o.y >= 1 ? o.y : 1;
    return { shape, style };
  } catch {
    return null;
  }
}
